import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ProcessEvaluationService } from '../../service/process-evaluation.service';
import { catchError, map, mergeMap, of } from 'rxjs';
import { ProcessEvaluationActions } from './process-evaluation.actions';
import { GetProcessEvaluationQuery } from '../../../../graphql/generated';
import { MessageService } from 'primeng/api';
import { graphQlErrorMessage } from '../../shared/graphql-error.util';

@Injectable()
export class ProcessEvaluationEffects {

  loadProcessEvaluation$ = createEffect(
    (rs: ProcessEvaluationService = inject(ProcessEvaluationService)) => {
      return this.actions$.pipe(

        ofType(ProcessEvaluationActions.loadProcessEvaluation),
        mergeMap(({ campaignId, cptIds, parameters, fields, legalEntitiesId }) =>
          rs.getProcessEvaluation(campaignId, cptIds, parameters, legalEntitiesId).pipe(
            map((riskMap) => {
              return ProcessEvaluationActions.loadProcessEvaluationSuccess({
                riskMap: riskMap as GetProcessEvaluationQuery['evaluationTreeProcessView'],
                fields
              });
            }),
            catchError((error) => of(ProcessEvaluationActions.loadProcessEvaluationFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadFilters$ = createEffect(
    (rs: ProcessEvaluationService = inject(ProcessEvaluationService)) => {
      return this.actions$.pipe(
        ofType(ProcessEvaluationActions.loadAllProcessEvaluation),
        mergeMap(({ campaignId, cptIds, parameters, fields, legalEntitiesId }) =>
          rs.getFilters(cptIds).pipe(
            mergeMap((riskMap) => {
              const successAction = ProcessEvaluationActions.loadAllProcessEvaluationSuccess({
                riskMap,
                fields
              });

              const anotherAction = ProcessEvaluationActions.loadProcessEvaluation({
                campaignId,
                cptIds,
                fields,
                parameters,
                legalEntitiesId
              });

              return [successAction, anotherAction]; // <-- Return multiple actions
            }),
            catchError((error) =>
              of(ProcessEvaluationActions.loadAllProcessEvaluationFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  updateProcessEvaluation$ = createEffect(
    (
      rs: ProcessEvaluationService = inject(ProcessEvaluationService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(ProcessEvaluationActions.updateProcessEvaluation),
        mergeMap(({ icsId, impactId, likelihoodId, riskId, riskMapId, legalEntitiesId }) =>
          rs.updateProcessEvaluation(riskMapId, riskId, impactId, likelihoodId, icsId, legalEntitiesId).pipe(
            map((riskMap) => {
              return ProcessEvaluationActions.updateProcessEvaluationSuccess({
                riskMap: riskMap
              });
            }),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Evaluation Error',
                detail: graphQlErrorMessage(error, 'Unable to update the evaluation'),
                life: 10000
              });
              return of(ProcessEvaluationActions.updateProcessEvaluationFailure({ error }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  exportEvaluationProcess$ = createEffect(
    (as: ProcessEvaluationService = inject(ProcessEvaluationService)) => {
      return this.actions$.pipe(
        ofType(ProcessEvaluationActions.exportEvaluationProcessTree),
        mergeMap(({ campaignId, cptIds, parameters, timeZone, legalEntitiesId }) =>
          as.downloadProcessEvaluation(campaignId, cptIds, parameters, timeZone, legalEntitiesId
          ).pipe(
            map((file) => {
              this.downloadFile(
                file!.data![0]!.content,
                file!.data![0]!.filename
              );
              return ProcessEvaluationActions.exportEvaluationProcessTreeSuccess({
                riskMap: file
              });
            }),
            catchError((error) => of(ProcessEvaluationActions.exportEvaluationProcessTreeFailure({ error })))
          )
        )
      )
    }
  )


  async downloadFile(response: string, fileName: string | undefined) {
    const typeFormat = {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
    const link = window.document.createElement("a");
    const blob = this.base64ToBlob(response, typeFormat.type); // Cambia il tipo MIME
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName ?? "fileExport";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }



  constructor(private actions$: Actions) { }
}
